import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Shield, Users, DollarSign, Star, CheckCircle2, Briefcase, ArrowRight,
  Code, Cloud, ShieldAlert, Zap, Globe, Award, Upload, FileText, X, Loader2, Linkedin, Sparkles
} from 'lucide-react';

const SPECIALTIES = [
  { id: 'it_support', name: 'IT Support & Help Desk', icon: Briefcase },
  { id: 'cybersecurity', name: 'Cybersecurity', icon: ShieldAlert },
  { id: 'scada_ot', name: 'SCADA / OT Security', icon: Zap },
  { id: 'cloud', name: 'Cloud & DevOps', icon: Cloud },
  { id: 'compliance', name: 'Compliance & Audit', icon: Shield },
  { id: 'web_dev', name: 'Web Development', icon: Code },
  { id: 'managed_services', name: 'Managed IT', icon: Globe },
];

export default function JoinNetwork() {
  const [mode, setMode] = useState(null); // 'freelancer' | 'partner'
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [freelancerForm, setFreelancerForm] = useState({
    name: '', email: '', phone: '', specialty: 'it_support', skills: '',
    experience_years: '', hourly_rate: '', portfolio_url: '', linkedin: '',
    certifications: '', bio: '', availability: 'full_time',
  });

  // Agreement state
  const [agreement, setAgreement] = useState(null); // { version, title, text, effective_date }
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);

  // Document uploads (resume + LinkedIn profile PDF)
  const [resumeDoc, setResumeDoc] = useState(null); // { url, filename, size, parsed }
  const [linkedinDoc, setLinkedinDoc] = useState(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [uploadingLinkedin, setUploadingLinkedin] = useState(false);
  // Track which fields were auto-filled by AI so we can badge them
  const [autoFilled, setAutoFilled] = useState({});
  const resumeInputRef = useRef(null);
  const linkedinInputRef = useRef(null);

  // Small pill that flags an AI-prefilled field
  const AIBadge = () => (
    <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#D4A843]/15 text-[#D4A843] uppercase tracking-wide align-middle">
      <Sparkles className="w-2.5 h-2.5" /> AI
    </span>
  );

  const [partnerForm, setPartnerForm] = useState({
    business_name: '', contact_name: '', email: '', phone: '',
    website: '', partner_type: 'referral', company_size: '', industry: '', message: '',
  });

  // Load the current Independent Contractor Agreement once the user opens the freelancer form
  useEffect(() => {
    if (mode !== 'freelancer' || agreement) return;
    (async () => {
      try {
        const { data } = await api.get('/api/onboard/freelancer/agreement');
        setAgreement(data);
      } catch (err) {
        console.warn('[agreement] fetch failed', err);
        toast.error('Could not load contractor agreement — please refresh');
      }
    })();
  }, [mode, agreement]);

  const uploadDocument = async (docType, file, setDoc, setBusy, inputRef) => {
    if (!file) return;
    // 10MB cap matches backend limit
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append('document_type', docType);
    fd.append('file', file);
    try {
      const { data } = await api.post('/api/onboard/freelancer/upload-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDoc({ url: data.url, filename: data.filename, size: data.size, parsed: data.parsed || null });

      // Auto-fill form fields from AI extraction (only fill empty fields so we
      // never clobber what the applicant typed). Tracks which fields were
      // auto-filled so the UI can show an "AI-extracted" badge.
      if (data.parsed) {
        const newAuto = { ...autoFilled };
        setFreelancerForm(prev => {
          const next = { ...prev };
          if (!prev.name && data.parsed.name) { next.name = data.parsed.name; newAuto.name = true; }
          if (!prev.email && data.parsed.email) { next.email = data.parsed.email; newAuto.email = true; }
          if (!prev.phone && data.parsed.phone) { next.phone = data.parsed.phone; newAuto.phone = true; }
          if (!prev.linkedin && data.parsed.linkedin) { next.linkedin = data.parsed.linkedin; newAuto.linkedin = true; }
          if (!prev.experience_years && data.parsed.years_experience) {
            next.experience_years = String(data.parsed.years_experience);
            newAuto.experience_years = true;
          }
          if (!prev.skills && Array.isArray(data.parsed.skills) && data.parsed.skills.length) {
            next.skills = data.parsed.skills.slice(0, 20).join(', ');
            newAuto.skills = true;
          }
          if (!prev.certifications && Array.isArray(data.parsed.certifications) && data.parsed.certifications.length) {
            next.certifications = data.parsed.certifications.slice(0, 15).join(', ');
            newAuto.certifications = true;
          }
          if (!prev.bio && data.parsed.summary) { next.bio = data.parsed.summary; newAuto.bio = true; }
          return next;
        });
        setAutoFilled(newAuto);
        const prettyName = docType === 'resume' ? 'Resume' : 'LinkedIn PDF';
        toast.success(`${prettyName} uploaded · AI pre-filled your profile`);
      } else {
        const prettyName = docType === 'resume' ? 'Resume' : 'LinkedIn PDF';
        toast.success(`${prettyName} uploaded`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
      if (inputRef?.current) inputRef.current.value = '';
    } finally {
      setBusy(false);
    }
  };

  const submitFreelancer = async () => {
    if (!freelancerForm.name || !freelancerForm.email) { toast.error('Name and email required'); return; }
    if (!agreementAccepted) { toast.error('You must accept the Independent Contractor Agreement'); return; }
    if (!agreement?.version) { toast.error('Agreement not loaded — please refresh'); return; }
    if (!resumeDoc && !linkedinDoc) { toast.error('Please upload a resume or LinkedIn profile PDF'); return; }
    setSubmitting(true);
    try {
      await api.post('/api/onboard/freelancer/apply', {
        ...freelancerForm,
        skills: freelancerForm.skills.split(',').map(s => s.trim().toLowerCase()).filter(Boolean),
        experience_years: parseInt(freelancerForm.experience_years) || 0,
        hourly_rate: parseInt(freelancerForm.hourly_rate) || 0,
        certifications: freelancerForm.certifications.split(',').map(s => s.trim()).filter(Boolean),
        resume_url: resumeDoc?.url || '',
        resume_filename: resumeDoc?.filename || '',
        linkedin_pdf_url: linkedinDoc?.url || '',
        linkedin_pdf_filename: linkedinDoc?.filename || '',
        parsed_resume: resumeDoc?.parsed || null,
        parsed_linkedin: linkedinDoc?.parsed || null,
        agreement_accepted: true,
        agreement_version: agreement.version,
      });
      setSubmitted(true);
      toast.success('Application submitted!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Submission failed'); }
    setSubmitting(false);
  };

  const submitPartner = async () => {
    if (!partnerForm.business_name || !partnerForm.email) { toast.error('Business name and email required'); return; }
    setSubmitting(true);
    try {
      await api.post('/api/onboard/partner/apply', partnerForm);
      setSubmitted(true);
      toast.success('Application submitted!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Submission failed'); }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-[#D4A843] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-[#0A0A0A]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Application Received</h1>
          <p className="text-gray-400 mb-6">We'll review your application within 48 hours and get back to you with next steps.</p>
          <Link to="/" className="text-[#D4A843] hover:underline text-sm">Back to TechOps</Link>
        </div>
      </div>
    );
  }

  // Landing — choose freelancer or partner
  if (!mode) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]" data-testid="join-network">
        {/* Header */}
        <nav className="flex items-center justify-between p-6 max-w-6xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#D4A843] rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-[#0A0A0A]" />
            </div>
            <span className="text-white font-bold text-lg">TechOps</span>
          </Link>
          <Link to="/login"><Button variant="outline" className="text-xs border-[#D4A843] text-[#D4A843] hover:bg-[#D4A843] hover:text-[#0A0A0A] rounded-lg">Sign In</Button></Link>
        </nav>

        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Join the EIGOO Network</h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">Earn money as a freelance IT/cyber specialist or grow your business through our referral partner program.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Freelancer Card */}
            <div onClick={() => setMode('freelancer')} className="bg-[#111] border border-gray-800 rounded-2xl p-8 cursor-pointer hover:border-[#D4A843] transition-all group" data-testid="choose-freelancer">
              <Users className="w-10 h-10 text-[#D4A843] mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Freelancer</h2>
              <p className="text-gray-400 mb-6">Join our pool of vetted IT & cybersecurity specialists. Get matched with clients, work on your schedule, get paid.</p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle2 className="w-4 h-4 text-[#D4A843]" /> AI-matched to relevant projects</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle2 className="w-4 h-4 text-[#D4A843]" /> Set your own hourly rate</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle2 className="w-4 h-4 text-[#D4A843]" /> Keep 70% of every project</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle2 className="w-4 h-4 text-[#D4A843]" /> Build your reputation with ratings</li>
              </ul>
              <div className="flex items-center text-[#D4A843] font-semibold group-hover:gap-3 transition-all gap-2">
                Apply Now <ArrowRight className="w-4 h-4" />
              </div>
            </div>

            {/* Partner Card */}
            <div onClick={() => setMode('partner')} className="bg-[#111] border border-gray-800 rounded-2xl p-8 cursor-pointer hover:border-[#7C3AED] transition-all group" data-testid="choose-partner">
              <DollarSign className="w-10 h-10 text-[#7C3AED] mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Referral Partner</h2>
              <p className="text-gray-400 mb-6">Earn commissions by referring clients to TechOps. Perfect for MSPs, consultants, and IT service providers.</p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle2 className="w-4 h-4 text-[#7C3AED]" /> 10-20% commission on platform earnings</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle2 className="w-4 h-4 text-[#7C3AED]" /> Tiered rewards (Bronze → Platinum)</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle2 className="w-4 h-4 text-[#7C3AED]" /> Unique EIGOO-XXXX referral code</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle2 className="w-4 h-4 text-[#7C3AED]" /> Real-time earnings dashboard</li>
              </ul>
              <div className="flex items-center text-[#7C3AED] font-semibold group-hover:gap-3 transition-all gap-2">
                Apply Now <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Freelancer Form
  if (mode === 'freelancer') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12 px-6" data-testid="freelancer-form">
        <div className="max-w-2xl mx-auto">
          <button onClick={() => setMode(null)} className="text-sm text-gray-400 hover:text-white mb-6 flex items-center gap-1">&larr; Back</button>
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-1">Freelancer Application</h2>
            <p className="text-gray-400 text-sm mb-6">Fill out your details. We review applications within 48 hours.</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-400">Full Name *</Label><Input value={freelancerForm.name} onChange={e => setFreelancerForm({...freelancerForm, name: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" data-testid="fl-name" /></div>
                <div><Label className="text-xs text-gray-400">Email *</Label><Input type="email" value={freelancerForm.email} onChange={e => setFreelancerForm({...freelancerForm, email: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" data-testid="fl-email" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-400">Phone</Label><Input value={freelancerForm.phone} onChange={e => setFreelancerForm({...freelancerForm, phone: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" /></div>
                <div><Label className="text-xs text-gray-400">Specialty</Label>
                  <select value={freelancerForm.specialty} onChange={e => setFreelancerForm({...freelancerForm, specialty: e.target.value})} className="mt-1 w-full h-9 rounded-lg bg-[#0A0A0A] border border-gray-700 text-white px-3 text-xs" data-testid="fl-specialty">
                    {SPECIALTIES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div><Label className="text-xs text-gray-400">Skills (comma-separated){autoFilled.skills && <AIBadge />}</Label><Input value={freelancerForm.skills} onChange={e => setFreelancerForm({...freelancerForm, skills: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" placeholder="e.g., python, aws, penetration testing, docker" data-testid="fl-skills" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-gray-400">Experience (years){autoFilled.experience_years && <AIBadge />}</Label><Input type="number" value={freelancerForm.experience_years} onChange={e => setFreelancerForm({...freelancerForm, experience_years: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" data-testid="fl-experience" /></div>
                <div><Label className="text-xs text-gray-400">Hourly Rate ($)</Label><Input type="number" value={freelancerForm.hourly_rate} onChange={e => setFreelancerForm({...freelancerForm, hourly_rate: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" data-testid="fl-rate" /></div>
                <div><Label className="text-xs text-gray-400">Availability</Label>
                  <select value={freelancerForm.availability} onChange={e => setFreelancerForm({...freelancerForm, availability: e.target.value})} className="mt-1 w-full h-9 rounded-lg bg-[#0A0A0A] border border-gray-700 text-white px-3 text-xs">
                    <option value="full_time">Full Time</option><option value="part_time">Part Time</option><option value="contract">Contract Only</option>
                  </select>
                </div>
              </div>
              <div><Label className="text-xs text-gray-400">Certifications (comma-separated){autoFilled.certifications && <AIBadge />}</Label><Input value={freelancerForm.certifications} onChange={e => setFreelancerForm({...freelancerForm, certifications: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" placeholder="e.g., CISSP, AWS Solutions Architect, CompTIA Security+" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-400">Portfolio URL</Label><Input value={freelancerForm.portfolio_url} onChange={e => setFreelancerForm({...freelancerForm, portfolio_url: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" /></div>
                <div><Label className="text-xs text-gray-400">LinkedIn{autoFilled.linkedin && <AIBadge />}</Label><Input value={freelancerForm.linkedin} onChange={e => setFreelancerForm({...freelancerForm, linkedin: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" /></div>
              </div>
              <div><Label className="text-xs text-gray-400">Bio{autoFilled.bio && <AIBadge />}</Label><textarea value={freelancerForm.bio} onChange={e => setFreelancerForm({...freelancerForm, bio: e.target.value})} className="mt-1 w-full bg-[#0A0A0A] border border-gray-700 text-white rounded-lg px-3 py-2 text-xs min-h-[80px]" placeholder="Tell us about your experience and what you're looking for..." data-testid="fl-bio" /></div>

              {/* Document uploads */}
              <div className="pt-2 border-t border-gray-800">
                <p className="text-xs text-gray-300 font-semibold mb-2">Upload Your Credentials</p>
                <p className="text-[11px] text-gray-500 mb-3">Attach a resume and/or your LinkedIn profile export. At least one is required.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Resume */}
                  <div className={`rounded-lg border p-3 ${resumeDoc ? 'border-[#D4A843] bg-[#D4A843]/5' : 'border-gray-700 bg-[#0A0A0A]'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-3.5 h-3.5 text-[#D4A843]" />
                      <span className="text-xs font-semibold text-white">Resume</span>
                      <span className="text-[10px] text-gray-500 ml-auto">PDF or DOCX · ≤10MB</span>
                    </div>
                    {!resumeDoc ? (
                      <>
                        <input
                          ref={resumeInputRef}
                          type="file"
                          accept=".pdf,.docx,.doc,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={e => uploadDocument('resume', e.target.files?.[0], setResumeDoc, setUploadingResume, resumeInputRef)}
                          className="hidden"
                          data-testid="fl-resume-input"
                        />
                        <Button
                          type="button"
                          onClick={() => resumeInputRef.current?.click()}
                          disabled={uploadingResume}
                          className="w-full bg-[#111] hover:bg-[#1a1a1a] border border-gray-700 text-white text-xs rounded-lg"
                          data-testid="fl-resume-btn"
                        >
                          {uploadingResume ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Uploading…</> : <><Upload className="w-3 h-3 mr-1.5" /> Choose Resume</>}
                        </Button>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#D4A843] flex-shrink-0" />
                        <span className="text-[11px] text-white truncate flex-1">{resumeDoc.filename}</span>
                        <button type="button" onClick={() => { setResumeDoc(null); if (resumeInputRef.current) resumeInputRef.current.value = ''; }} className="text-gray-400 hover:text-red-400" data-testid="fl-resume-clear">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* LinkedIn PDF */}
                  <div className={`rounded-lg border p-3 ${linkedinDoc ? 'border-[#D4A843] bg-[#D4A843]/5' : 'border-gray-700 bg-[#0A0A0A]'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" />
                      <span className="text-xs font-semibold text-white">LinkedIn Profile PDF</span>
                      <span className="text-[10px] text-gray-500 ml-auto">PDF only · ≤10MB</span>
                    </div>
                    {!linkedinDoc ? (
                      <>
                        <input
                          ref={linkedinInputRef}
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={e => uploadDocument('linkedin_pdf', e.target.files?.[0], setLinkedinDoc, setUploadingLinkedin, linkedinInputRef)}
                          className="hidden"
                          data-testid="fl-linkedin-input"
                        />
                        <Button
                          type="button"
                          onClick={() => linkedinInputRef.current?.click()}
                          disabled={uploadingLinkedin}
                          className="w-full bg-[#111] hover:bg-[#1a1a1a] border border-gray-700 text-white text-xs rounded-lg"
                          data-testid="fl-linkedin-btn"
                        >
                          {uploadingLinkedin ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Uploading…</> : <><Upload className="w-3 h-3 mr-1.5" /> Upload LinkedIn PDF</>}
                        </Button>
                        <p className="text-[10px] text-gray-500 mt-1.5 leading-snug">On LinkedIn: <span className="text-gray-400">More → Save to PDF</span> on your profile</p>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#D4A843] flex-shrink-0" />
                        <span className="text-[11px] text-white truncate flex-1">{linkedinDoc.filename}</span>
                        <button type="button" onClick={() => { setLinkedinDoc(null); if (linkedinInputRef.current) linkedinInputRef.current.value = ''; }} className="text-gray-400 hover:text-red-400" data-testid="fl-linkedin-clear">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Independent Contractor Agreement */}
              <div className="pt-2 border-t border-gray-800">
                <p className="text-xs text-gray-300 font-semibold mb-2">Independent Contractor Agreement</p>
                {agreement ? (
                  <>
                    <div className="rounded-lg border border-gray-700 bg-[#0A0A0A] p-3 max-h-56 overflow-y-auto text-[11px] leading-relaxed text-gray-300 whitespace-pre-wrap font-mono" data-testid="fl-agreement-text">
                      {agreement.text}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-[10px] text-gray-500">Version {agreement.version} · Effective {agreement.effective_date}</p>
                      <button type="button" onClick={() => setShowAgreement(true)} className="text-[10px] text-[#D4A843] hover:underline" data-testid="fl-agreement-expand">View full screen</button>
                    </div>
                    <label className="flex items-start gap-3 mt-3 cursor-pointer group" data-testid="fl-agreement-label">
                      <Checkbox
                        checked={agreementAccepted}
                        onCheckedChange={(v) => setAgreementAccepted(!!v)}
                        className="mt-0.5 border-gray-600 data-[state=checked]:bg-[#D4A843] data-[state=checked]:border-[#D4A843] data-[state=checked]:text-[#0A0A0A]"
                        data-testid="fl-agreement-check"
                      />
                      <span className="text-xs text-gray-300 group-hover:text-white">
                        I have read, understood, and agree to be bound by the <span className="text-[#D4A843] font-semibold">EIGOO Inc. Independent Contractor Agreement</span> (version {agreement.version}). My electronic signature, timestamp, and IP address will be recorded as evidence of acceptance.
                      </span>
                    </label>
                  </>
                ) : (
                  <div className="rounded-lg border border-gray-700 bg-[#0A0A0A] p-4 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#D4A843]" />
                    <span className="text-xs text-gray-400">Loading contractor agreement…</span>
                  </div>
                )}
              </div>

              <Button
                onClick={submitFreelancer}
                disabled={submitting || !agreementAccepted || !agreement || (!resumeDoc && !linkedinDoc)}
                className="w-full bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg py-3 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="fl-submit"
              >
                {submitting ? 'Submitting…' : 'Submit Application'}
              </Button>
            </div>
          </div>
        </div>

        {/* Full-screen agreement modal */}
        {showAgreement && agreement && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowAgreement(false)} data-testid="fl-agreement-modal">
            <div className="bg-[#111] border border-gray-800 rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-gray-800">
                <div>
                  <h3 className="text-base font-bold text-white">{agreement.title}</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Version {agreement.version} · Effective {agreement.effective_date}</p>
                </div>
                <button onClick={() => setShowAgreement(false)} className="text-gray-400 hover:text-white" data-testid="fl-agreement-close"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 overflow-y-auto text-[12px] leading-relaxed text-gray-300 whitespace-pre-wrap">{agreement.text}</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Partner Form
  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-6" data-testid="partner-form">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => setMode(null)} className="text-sm text-gray-400 hover:text-white mb-6 flex items-center gap-1">&larr; Back</button>
        <div className="bg-[#111] border border-gray-800 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-1">Partner Application</h2>
          <p className="text-gray-400 text-sm mb-6">Join the EIGOO referral partner program. Earn 10-20% commission on every referred sale.</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-400">Business Name *</Label><Input value={partnerForm.business_name} onChange={e => setPartnerForm({...partnerForm, business_name: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" data-testid="pt-biz" /></div>
              <div><Label className="text-xs text-gray-400">Contact Name *</Label><Input value={partnerForm.contact_name} onChange={e => setPartnerForm({...partnerForm, contact_name: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" data-testid="pt-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-400">Email *</Label><Input type="email" value={partnerForm.email} onChange={e => setPartnerForm({...partnerForm, email: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" data-testid="pt-email" /></div>
              <div><Label className="text-xs text-gray-400">Phone</Label><Input value={partnerForm.phone} onChange={e => setPartnerForm({...partnerForm, phone: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-400">Website</Label><Input value={partnerForm.website} onChange={e => setPartnerForm({...partnerForm, website: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" /></div>
              <div><Label className="text-xs text-gray-400">Partner Type</Label>
                <select value={partnerForm.partner_type} onChange={e => setPartnerForm({...partnerForm, partner_type: e.target.value})} className="mt-1 w-full h-9 rounded-lg bg-[#0A0A0A] border border-gray-700 text-white px-3 text-xs" data-testid="pt-type">
                  <option value="referral">Referral Partner</option><option value="reseller">Reseller</option><option value="white_label">White-Label</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-400">Company Size</Label>
                <select value={partnerForm.company_size} onChange={e => setPartnerForm({...partnerForm, company_size: e.target.value})} className="mt-1 w-full h-9 rounded-lg bg-[#0A0A0A] border border-gray-700 text-white px-3 text-xs">
                  <option value="">Select...</option><option value="1-10">1-10</option><option value="11-50">11-50</option><option value="51-200">51-200</option><option value="200+">200+</option>
                </select>
              </div>
              <div><Label className="text-xs text-gray-400">Industry</Label><Input value={partnerForm.industry} onChange={e => setPartnerForm({...partnerForm, industry: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" placeholder="e.g., IT Services, Healthcare, Manufacturing" /></div>
            </div>
            <div><Label className="text-xs text-gray-400">Message</Label><textarea value={partnerForm.message} onChange={e => setPartnerForm({...partnerForm, message: e.target.value})} className="mt-1 w-full bg-[#0A0A0A] border border-gray-700 text-white rounded-lg px-3 py-2 text-xs min-h-[80px]" placeholder="Tell us about your business and how you'd like to partner..." data-testid="pt-msg" /></div>
            <Button onClick={submitPartner} disabled={submitting} className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-lg py-3" data-testid="pt-submit">
              {submitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
